/* eslint-disable max-lines-per-function */
import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MultiLevelMenuItemProps } from '@bl/components/dist/menu-item/types';
import { TranslocoModule } from '@jsverse/transloco';
import { createPopup, type Popover } from '@typeform/embed';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest, filter, Observable } from 'rxjs';
import { CountryType, isSweden } from 'src/app/core/entity/locale';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { AuthSelectors } from 'src/app/core/state/auth/auth.selectors';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { UserType } from 'src/app/core/state/types';
import { BlFrontendButtonMenuComponent } from 'src/app/core/wrappers/bl-frontend-button-menu.component';
import { FirmSettingsDialog } from 'src/app/firm-settings/firm.settings.dialog';
import { environment } from 'src/environments/environment';
import { openTranslatableDialog } from '../../misc/openDialog';
import { NavbarComponentTranslationType } from './types';

@Component({
  selector: 'app-help-menu',
  template: `
    <bl-frontend-button-menu
      [text]="i18n().help"
      [menuItems]="menuItems"
      variant="tonal"
      [maxMenuHeight]="320"></bl-frontend-button-menu>
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BlFrontendButtonMenuComponent, TranslocoModule],
  providers: [getTranslationProvider('NavbarComponent')],
})
export class HelpMenuComponent extends TranslatableComponent<NavbarComponentTranslationType> {
  menuItems: MultiLevelMenuItemProps[];

  protected i18n: WritableSignal<NavbarComponentTranslationType> = signal({} as NavbarComponentTranslationType);

  private dialogService = inject(DialogService);
  private popup: Popover;
  private loggedInUser: UserType | undefined;

  constructor() {
    super();
    this.initMenuItems();
  }

  private initMenuItems(): void {
    combineLatest([
      this.store.select(FirmSelectors.selectFirm),
      this.store.select(AuthSelectors.selectAuthUser),
      this.getFirmCountryCodeObservable(),
      this.translationsLoaded$.pipe(filter(Boolean)),
    ])
      .pipe(
        filter(([firm, user]) => Boolean(firm) && Boolean(user)),
        takeUntilDestroyed(),
      )
      .subscribe(([firm, user, firmCountryCode, translations]) => {
        this.loggedInUser = user;
        this.i18n.set(translations);
        this.menuItems = this.initHelpItems(firmCountryCode);

        this.popup = createPopup(environment.forms.giveFeedback[user.languageCode], {
          size: 50,
          hideFooter: true,
          hidden: {
            title: 'Workflow',
            firmId: String(firm.id),
            userId: String(user.id),
            countryCode: firmCountryCode,
            formType: 'popup',
          },
        });
      });
  }

  private getFirmCountryCodeObservable(): Observable<CountryType> {
    return this.store.select(FirmSelectors.selectCountryCode).pipe(filter(Boolean));
  }

  private initHelpItems(countryCode: CountryType): MultiLevelMenuItemProps[] {
    return isSweden(countryCode) ? this.getSwedishHelpMenuItems() : this.getDutchHelpMenuItems();
  }

  private getSwedishHelpMenuItems(): MultiLevelMenuItemProps[] {
    return [
      {
        id: 'questionsAndAnswers',
        text: this.i18n().questionsAndAnswers,
        onAction: this.createOpenUrlAction('https://forum.se.bjornlunden.com/'),
      },
      {
        id: 'manual',
        text: this.i18n().manual,
        onAction: this.createOpenUrlAction('https://support.se.bjornlunden.com/'),
      },
      {
        id: 'filmedGuides',
        text: this.i18n().filmedGuides,
        onAction: this.createOpenUrlAction('https://www.youtube.com/playlist?list=PL009yjRM5nBhrjxSv7xa3fvehLRNf0kZH'),
      },
      {
        id: 'chatWithSupport',
        text: this.i18n().chatWithSupport,
        onAction: () => this.startChat(),
      },
      {
        id: 'remoteSupport',
        text: this.i18n().remoteSupport,
        onAction: this.createOpenUrlAction('https://download.blinfo.se/v2/public/fjarrsupport.exe'),
      },
      {
        id: 'facebookUserGroup',
        text: this.i18n().facebookUserGroup,
        onAction: this.createOpenUrlAction('https://www.facebook.com/groups/bjornlunden/'),
      },
      // {
      //   separator: true,
      // },
      {
        id: 'downloadProgram',
        text: this.i18n().downloadProgram,
        onAction: () =>
          openTranslatableDialog({
            deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
            componentType: FirmSettingsDialog,
            data: { tabIdToOpen: 'tabFirmSystemSettings' },
          }),
      },
      // {
      //   separator: true,
      // },
      {
        id: 'giveFeedback',
        text: this.i18n().giveFeedback,
        onAction: () => {
          this.popup.open();
        },
      },
    ];
  }

  private getDutchHelpMenuItems(): MultiLevelMenuItemProps[] {
    return [
      {
        id: 'questionsAndAnswers',
        text: this.i18n().questionsAndAnswers,
        onAction: this.createOpenUrlAction('https://forum.nl.bjornlunden.com/'),
      },
      {
        id: 'manual',
        text: this.i18n().manual,
        onAction: this.createOpenUrlAction('https://support.nl.bjornlunden.com/'),
      },
      {
        id: 'remoteSupport',
        text: this.i18n().remoteSupport,
        onAction: this.createOpenUrlAction('https://get.teamviewer.com/kingsoftware'),
      },
      // {
      //   separator: true,
      // },
      {
        id: 'giveFeedback',
        text: this.i18n().giveFeedback,
        onAction: () => {
          this.popup.open();
        },
      },
    ];
  }

  private createOpenUrlAction(url: string): () => void {
    return () => window.open(url, '_blank', 'noopener');
  }

  private startChat = (): void => {
    if (!window.$kundo_chat) {
      return;
    }

    if (this.loggedInUser) {
      window.$kundo_chat.user = {
        name: `${this.loggedInUser.name} - ${this.loggedInUser.firm?.name} - ${this.loggedInUser.firm?.corporateIdentity}`,
        email: `${this.loggedInUser.email}`,
      };
    }

    window.$kundo_chat.start_chat();
  };
}
